import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Centered } from '../../components/Centered.tsx';
import { useTranslate } from '../../i18n/index.tsx';
import { redeemMagicLink } from '../../services/auth.service.ts';
import { useAuthStore } from '../../stores/auth.store.ts';

/**
 * Redeems the token from an emailed link and forwards the person to whatever
 * the link was for.
 *
 * A token is single use, so redemption must be attempted exactly once. A plain
 * cancellation flag is not enough: React's StrictMode runs the effect twice in
 * development, and the second attempt would find the token already spent and
 * report a dead link. The ref makes the attempt itself idempotent, which also
 * covers a remount or a fast double navigation in production.
 */
export const MagicLinkPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const restore = useAuthStore((state) => state.restore);
  const t = useTranslate();

  const attempted = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token || attempted.current === token) return;
    attempted.current = token;

    (async () => {
      try {
        const result = await redeemMagicLink(token);
        await restore();
        // The landing route decides where a session belongs, the same way it
        // does after a password sign-in. Going straight to the dashboard sent
        // every leader to the one page their role cannot read: PARTICIPANT
        // holds `report:read:own` and not `progress:read`, so the first thing
        // somebody saw on being handed their report was "no tienes acceso".
        navigate(result.surveyId ? '/encuesta' : '/', { replace: true });
      } catch {
        setFailed(true);
      }
    })();
  }, [token, navigate, restore]);

  if (failed) {
    return (
      <Centered>
        {t('survey.linkExpired')} {t('survey.linkExpiredHelp')}
      </Centered>
    );
  }

  return <Centered>{t('common.loading')}</Centered>;
};
